import { AMOUNT_INTENTS, type AmountIntent } from "@kmstories/shared";
import { useState, type FormEvent } from "react";
import { useT } from "../i18n/LocaleProvider.js";
import { api, ApiError } from "../lib/api.js";
import { useDocumentTitle } from "../lib/useDocumentTitle.js";
import { useFormToken } from "../lib/useFormToken.js";

type Help = "funding" | "screening" | "connect" | "volunteer";

export function SupportPage() {
  const { m, locale } = useT();
  useDocumentTitle(m.meta.pages.support);
  const { getToken } = useFormToken("support");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [help, setHelp] = useState<Record<Help, boolean>>({ funding: false, screening: false, connect: false, volunteer: false });
  const [amount, setAmount] = useState<AmountIntent | "">("");
  const [message, setMessage] = useState("");
  const [consentContact, setConsentContact] = useState(false);
  const [consentUpdates, setConsentUpdates] = useState(false);
  const [state, setState] = useState<"idle" | "busy" | "done">("idle");
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!consentContact) {
      setError(m.share.errors.consentRequired);
      return;
    }
    const form = e.currentTarget as HTMLFormElement;
    setState("busy");
    setError(null);
    try {
      const formToken = await getToken();
      const hp = form.elements.namedItem("fax") as HTMLInputElement | null;
      await api.post("/api/support-interest", {
        email,
        name: name.trim() || undefined,
        help,
        amountIntent: help.funding && amount ? amount : undefined,
        message: message.trim() || undefined,
        consentContact: true,
        consentUpdates,
        locale,
        formToken,
        fax: hp?.value ?? "",
      });
      setState("done");
    } catch (err) {
      setState("idle");
      if (err instanceof ApiError && err.status === 400) setError(err.issues.some((i) => i.path === "email") ? m.share.errors.emailInvalid : m.share.errors.invalid);
      else if (err instanceof ApiError && err.status === 429) setError(m.share.errors.rateLimited);
      else setError(m.common.error);
    }
  }

  return (
    <div className="container container--narrow">
      <div className="page-head">
        <h1>{m.support.heading}</h1>
        <p className="page-head__lede">
          <strong>{m.support.honest}</strong> {m.support.body}
        </p>
      </div>

      <div className="grid-2" style={{ marginBlockEnd: "2rem" }}>
        <div className="card">
          <h2 style={{ fontSize: "1.2rem" }}>{m.support.fundsHeading}</h2>
          <ul className="list-reset" style={{ display: "grid", gap: "0.5rem" }}>
            {(Object.keys(m.support.funds) as Array<keyof typeof m.support.funds>).map((k) => (
              <li key={k} className="pill">
                {m.support.funds[k]}
              </li>
            ))}
          </ul>
        </div>
        <figure className="figure figure--card">
          <img src="/art/suitcase.jpg" alt="" width="1200" height="1200" loading="lazy" />
          <figcaption className="card card--paper">
            <p style={{ margin: 0, fontSize: "1.1rem" }}>{m.support.early}</p>
          </figcaption>
        </figure>
      </div>

      {state === "done" ? (
        <p className="notice" role="status">
          {m.support.done}
        </p>
      ) : (
        <form className="form card" onSubmit={submit} noValidate>
          <h2 style={{ fontSize: "1.25rem", margin: 0 }}>{m.support.formHeading}</h2>
          <div className="field">
            <label className="field__label" htmlFor="s-email">
              {m.support.emailLabel}
            </label>
            <input id="s-email" className="input" type="email" inputMode="email" autoComplete="email" dir="ltr" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="field">
            <label className="field__label" htmlFor="s-name">
              {m.support.nameLabel}
            </label>
            <input id="s-name" className="input" dir="auto" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <fieldset className="field" style={{ border: 0, padding: 0, margin: 0 }}>
            <legend className="field__label">{m.support.helpLabel}</legend>
            {(Object.keys(help) as Help[]).map((k) => (
              <label key={k} className="check">
                <input type="checkbox" checked={help[k]} onChange={(e) => setHelp({ ...help, [k]: e.target.checked })} />
                <span>{m.support.help[k]}</span>
              </label>
            ))}
          </fieldset>
          {help.funding && (
            <div className="field">
              <label className="field__label" htmlFor="s-amount">
                {m.support.amountLabel}
              </label>
              <select id="s-amount" className="select" value={amount} onChange={(e) => setAmount(e.target.value as AmountIntent | "")}>
                <option value="">—</option>
                {AMOUNT_INTENTS.map((a) => (
                  <option key={a} value={a}>
                    {m.support.amounts[a]}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="field">
            <label className="field__label" htmlFor="s-msg">
              {m.support.messageLabel}
            </label>
            <textarea id="s-msg" className="textarea" dir="auto" maxLength={1000} value={message} onChange={(e) => setMessage(e.target.value)} />
          </div>
          <label className="check">
            <input type="checkbox" checked={consentContact} onChange={(e) => setConsentContact(e.target.checked)} required />
            <span>
              <span className="check__tag">{m.share.required}</span>
              <br />
              {m.support.consentContact}
            </span>
          </label>
          <label className="check">
            <input type="checkbox" checked={consentUpdates} onChange={(e) => setConsentUpdates(e.target.checked)} />
            <span>
              <span className="check__tag check__tag--optional">{m.share.optional}</span>
              <br />
              {m.support.consentUpdates}
            </span>
          </label>
          <div className="hp" aria-hidden="true">
            <label>
              Fax
              <input type="text" name="fax" tabIndex={-1} autoComplete="off" defaultValue="" />
            </label>
          </div>
          {error && (
            <p className="field__error" role="alert">
              {error}
            </p>
          )}
          <div className="actions" style={{ justifyContent: "flex-end" }}>
            <button className="btn btn--primary" type="submit" disabled={state === "busy"}>
              {m.support.submit}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
